import { Injectable } from "@angular/core";
import { NetworkService } from "~/app/core/network.service";
import { StorageService } from "~/app/core/storage.service";
import { SettingsService } from "~/app/core/settings.service";
import * as Log from "~/app/core/log";
import { InteractionService } from "~/app/core/interaction.service";
import { RPCService } from "~/app/core/rpc.service";
import { UserProfileService } from "~/app/core/user-profile-service/user-profile.service";
import { HttpTicketService } from "~/app/core/model/ticket-service/http-ticket-service";
import { SampleTicketService } from "~/app/core/model/ticket-service/sample-ticket-service";
import { InterfaceTicketService } from "~/app/core/model/ticket-service/interface-ticket-service";
import { NotificationService } from "~/app/core/notification.service";
import { momentNow, toDateStr, toMoment } from "~/app/core/model/date-str";
import { Observable, Subject } from "rxjs";
import { DailyTasks } from "~/app/core/model/ticket-info";
import {ToastService} from "~/app/core/toast.service";

@Injectable()
export class TicketService {
    private ticketDiff: Subject<number> = new Subject<number>();

    private readonly httpService: HttpTicketService;
    private readonly sampleService: SampleTicketService;

    private sample: boolean = null;
    private service: InterfaceTicketService;

    constructor(
        private readonly networkService: NetworkService,
        private readonly storageService: StorageService,
        private readonly settingsService: SettingsService,
        private readonly interactionService: InteractionService,
        private readonly userService: UserProfileService,
        private readonly toast: ToastService,
        private readonly notify: NotificationService,
        private readonly rpcService: RPCService
    ) {
        Log.debug(Log.Tag.TicketService, "CONSTRUCT TICKET SERVICE");
        this.sampleService = new SampleTicketService(settingsService, storageService);
        this.httpService = new HttpTicketService(networkService, settingsService, storageService, rpcService);

        this.service = this.httpService;
    }

    get authenticated(): boolean {
        return this.userService.isAuthenticated();
    }

    get useTickets(): boolean {
        return this.settingsService.loadSettings().gamificationEnabled;
    }

    get ticketCount(): number {
        return this.storageService.loadTicketCount(this.settingsService.loadSettings().env);
    }

    get referralEligible(): boolean {
        return !!this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env).referralEligible;
    }

    get referralNotified(): boolean {
        return !!this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env).referralNotified;
    }

    get personalReferralCode(): string {
        return this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env).referralCode;
    }

    get availableTicketCount(): number {
        return this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env).tickets;
    }

    get dailyProgress(): DailyTasks {
        return this.loadDailyTasksActual().dailyTasks;
    }

    getDailyProgressPercent(allowFiltering: boolean = true): number {
        const actual = this.loadDailyTasksActual();
        const settings = this.settingsService.loadSettings();
        const max =
            settings.earnTicketsVisitWebsite +
            settings.earnTicketsOpenOffer +
            settings.earnTicketsShare +
            settings.earnTicketsOpenApp;
        let total = 0;
        if (actual.dailyTasks) {
            total += actual.dailyTasks.openApp ? settings.earnTicketsOpenApp : 0;
            total += actual.dailyTasks.openOffer ? settings.earnTicketsOpenOffer : 0;
            total += actual.dailyTasks.share ? settings.earnTicketsShare : 0;
            total += actual.dailyTasks.webVisit ? settings.earnTicketsVisitWebsite : 0;
        }
        return (total / max);
    }

    ticketDiffObservable(): Observable<number> {
        return this.ticketDiff;
    }

    fetchTickets(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            return new Promise((resolve, reject) => {
                this.service.fetchTickets().then(() => {
                    this.updateTicketCount("Updated your ticket balance!", false, 5000).then(() => resolve(), (error) => reject(error));
                }, (error) => reject(error));
            });
        } else {
            return Promise.resolve();
        }
    }

    setTickets(ticketCount: number): Promise<void> {
        if (this.useTickets && this.authenticated) {
            // send the ticket total to the server
            return new Promise((resolve, reject) => {
                this.service.setTickets(ticketCount).then(() => {
                    this.updateTicketCount().then(() => resolve(), (error) => reject(error));
                }, (error) => reject(error));
            });
        } else {
            return Promise.resolve();
        }
    }

    notifyReferral(): Promise<void> {
        const tickets = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        tickets.referralNotified = true;
        this.storageService.storeTicketInfoActual(tickets, this.settingsService.loadSettings().env);
        return this.updateTicketCount(null);
    }

    spendTickets(spent: number, title: string, duration: number = 1000): Promise<void> {
        if (this.useTickets && this.authenticated) {
            return new Promise((resolve, reject) => {
                this.service.spendTickets(spent).then(() => {
                    this.updateTicketCount(title, true, duration).then(() => resolve(), (error) => reject(error));
                }, (error) => reject(error));
            });
        } else {
            return Promise.resolve();
        }
    }

    referredSignup(referralCode: string): Promise<boolean> {
        if (this.useTickets && this.authenticated && this.referralEligible && referralCode && referralCode.length > 0) {
            return new Promise((resolve, reject) => {
                // read ticket total from server
                this.service.verifyReferral(referralCode).then((result) => {
                    if (result) {
                        resolve(true);
                        this.updateTicketCount("Completed sign-up referral!");
                    } else {
                        resolve(false);
                    }
                }, (error) => resolve(false));
            });
        } else {
            return Promise.resolve(false);
        }
    }

    openedApp(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.openApp) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("openApp").then(() => {
                        this.updateTicketCount(actual.referralEligible ? "Welcome to WhutsFree!" : "Welcome back! Have some tickets.", false, actual.referralEligible ? 2500 : 1000).then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    openedOffer(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.openOffer) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("openOffer").then(() => {
                        this.updateTicketCount("Viewed the details of an offer!").then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    sharedOffer(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.share) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("share").then(() => {
                        this.updateTicketCount("Sharing an offer!").then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    filterOffers(): Promise<void> {
        if (this.useTickets) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.filterOffers) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("filter").then(() => {
                        this.updateTicketCount("Searched for offers!").then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    websiteVisit(): Promise<void> {
        if (this.useTickets) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.webVisit) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("webVisit").then(() => {
                        this.updateTicketCount("Visited a website!").then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    favoritedOffer(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            const actual = this.loadDailyTasksActual();
            if (!actual.dailyTasks.favorite) {
                return new Promise((resolve, reject) => {
                    this.service.earnTickets("favorite").then(() => {
                        return this.updateTicketCount("Favorited an offer!").then(() => resolve(), (error) => reject(error));
                    }, (error) => reject(error));
                });
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }

    redeemedLocalOffer(offerId: number, locationId: number, distance: number): Promise<void> {
        if (this.useTickets && this.authenticated) {
            return new Promise((resolve, reject) => {
                this.service.earnTicketsFromRedeem(offerId, locationId, distance).then(() => {
                    this.updateTicketCount("Redeemed a local offer!").then(() => resolve(), (error) => reject(error));
                }, (error) => reject(error));
            });
        } else {
            return Promise.resolve();
        }
    }

    redeemedOnlineOffer(offerId: number): Promise<void> {
        if (this.useTickets && this.authenticated) {
            return new Promise((resolve, reject) => {
                this.service.earnTicketsFromRedeem(offerId, null, null).then(() => {
                    this.updateTicketCount("Redeemed an online offer!").then(() => resolve(), (error) => reject(error));
                }, (error) => reject(error));
            });
        } else {
            return Promise.resolve();
        }
    }

    resetDailyTasks(): Promise<void> {
        if (this.useTickets && this.authenticated) {
            return this.service.resetDailyTasks();
        }
    }

    private loadDailyTasksActual() {
        return this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
    }

    private updateTicketCount(title: string = null, forceMessage: boolean = false, duration: number = 1000): Promise<void> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        let ticketCount = this.storageService.loadTicketCount(this.settingsService.loadSettings().env);
        const diff = actual.tickets - ticketCount;
        if (diff) {
            ticketCount = actual.tickets;
            this.storageService.storeTicketCount(ticketCount, this.settingsService.loadSettings().env);
        }

        return new Promise((resolve) => {
            if (diff > 0) {
                this.ticketDiff.next(diff);
                this.toast.toastSuccess({
                    header: title,
                    duration: duration,
                    message: "Earned " + diff + " ticket" + (diff !== 1 ? "s" : "") + ".",
                    onHide: () => resolve()
                });
            } else if (diff < 0) {
                this.ticketDiff.next(diff);
                this.toast.toastSuccess({
                    header: title,
                    duration: duration,
                    message: "Spent " + (-diff) + " ticket" + (diff !== -1 ? "s" : "") + ".",
                    onHide: () => resolve()
                });
            } else if (title && forceMessage) {
                this.toast.toastSuccess({
                    header: title,
                    duration: duration,
                    onHide: () => resolve()
                });
            } else {
                return resolve();
            }
        });
    }
}
